<template>
  <div class="navbar-wrapper">
    <nav class="app-navbar">
      <div class="navbar-left">
        <img :src="logo" alt="Sea Tow" class="navbar-logo" />
        <div class="navbar-divider"></div>
        <span class="navbar-app-name">Membership Portal</span>
      </div>

      <div class="navbar-right">
        <button class="theme-toggle" @click="toggleTheme" :title="darkMode ? 'Switch to light mode' : 'Switch to dark mode'">
          <svg v-if="!darkMode" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
          </svg>
          <svg v-else xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="5"/>
            <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
            <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
          </svg>
        </button>
        <div class="navbar-user" @click="showDropdown = !showDropdown" v-click-outside="closeDropdown">
          <div class="navbar-avatar">
            {{ userInitials }}
          </div>
          <span class="navbar-username">{{ getName }}</span>
          <svg class="navbar-chevron" :class="{ open: showDropdown }" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M4.427 5.427a.75.75 0 011.06-.073L8 7.594l2.513-2.24a.75.75 0 11.998 1.118l-3 2.672a.75.75 0 01-.998 0l-3-2.672a.75.75 0 01-.086-1.045z"/>
          </svg>

          <transition name="dropdown-fade">
            <div v-if="showDropdown" class="navbar-dropdown">
              <div class="dropdown-header">
                <div class="dropdown-avatar">{{ userInitials }}</div>
                <div class="dropdown-info">
                  <span class="dropdown-name">{{ getName }}</span>
                  <span class="dropdown-email">{{ userEmail }}</span>
                </div>
              </div>
              <div class="dropdown-divider"></div>
              <button class="dropdown-item" @click="logOut">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M2 3a1 1 0 011-1h5a1 1 0 010 2H4v8h4a1 1 0 110 2H3a1 1 0 01-1-1V3z"/>
                  <path d="M12.293 7H6.75a.75.75 0 000 1.5h5.543l-1.647 1.646a.75.75 0 101.06 1.061l2.829-2.828a.75.75 0 000-1.061l-2.829-2.828a.75.75 0 00-1.06 1.06L12.293 7z"/>
                </svg>
                Sign out
              </button>
              <div class="dropdown-version" :title="versionTitle">
                {{ versionLabel }}
              </div>
            </div>
          </transition>
        </div>
      </div>
    </nav>
  </div>
</template>

<script>
import auth from '../services/auth'
import graph from '../services/graph'
import logoLight from "@/assets/ST_logo_onwhite.png";
import logoDark from "@/assets/ST_logo_onblack.png";

export default {
  data() {
    return {
      user: null,
      darkMode: false,
      showDropdown: false,
    }
  },
  computed: {
    logo() {
      return this.darkMode ? logoDark : logoLight;
    },
    getName() {
      if (this.user) {
        return this.user.name;
      } else {
        return 'Invalid User'
      }
    },
    userInitials() {
      if (this.user && this.user.name) {
        // Strip org suffix like "| Sea Tow Int." before extracting initials
        const cleanName = this.user.name.split('|')[0].trim();
        const parts = cleanName.split(' ').filter(Boolean);
        if (parts.length >= 2) {
          return (parts[0][0] + parts[1][0]).toUpperCase();
        }
        return parts[0][0].toUpperCase();
      }
      return '?';
    },
    userEmail() {
      if (this.user && this.user.username) {
        return this.user.username;
      }
      return '';
    },
    // The commit the running bundle was built from, set in vue.config.js from
    // Heroku's SOURCE_VERSION (or git locally). Shown so a dealer reporting a
    // problem can say which build they are on.
    versionLabel() {
      const version = process.env.VUE_APP_VERSION;
      return version ? `Version ${version}` : 'Version unknown';
    },
    versionTitle() {
      const environment = process.env.VUE_APP_ENV;
      return environment ? `${this.versionLabel} (${environment})` : this.versionLabel;
    },
  },
  directives: {
    'click-outside': {
      bind(el, binding) {
        el._clickOutside = function(event) {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value();
          }
        };
        document.addEventListener('click', el._clickOutside);
      },
      unbind(el) {
        document.removeEventListener('click', el._clickOutside);
      },
    },
  },
  async created() {
    // Restore theme preference (only if user explicitly chose dark)
    if (localStorage.getItem('theme') === 'dark') {
      this.darkMode = true;
    }

    if (process.env.VUE_APP_O365_CLIENTID) {
      auth.configure(process.env.VUE_APP_O365_CLIENTID, false)
      this.user = auth.user()
    } else {
      this.error = 'VUE_APP_CLIENT_ID is not set, the app will not function!'
    }
  },
  watch: {
    auth: async function() {
      this.user = this.auth.user()
    },
    user: async function() {
      this.fetchGraphDetails()
    }
  },
  methods: {
    toggleTheme() {
      this.darkMode = !this.darkMode;
      const theme = this.darkMode ? 'dark' : 'light';
      document.documentElement.setAttribute('data-theme', theme);
      localStorage.setItem('theme', theme);
    },
    closeDropdown() {
      this.showDropdown = false;
    },
    isAuthenticated() {
      var user = auth.user();
      if (user !== undefined && user !== null) {
        this.$parent.isAuthenticated2 = true;
        return true;
      } else {
        this.$parent.isAuthenticated2 = false;
        return false;
      }
    },
    async fetchGraphDetails() {
      if (!this.user || this.user.username == 'demo@example.net') {
        return
      }

      try {
        this.graphDetails = await graph.getSelf()
        this.graphPhoto = await graph.getPhoto()
        this.accessToken = graph.getAccessToken()
      } catch (err) {
        this.error = err
      }
    },
    async logOut() {
      this.user = null
      this.graphDetails = null
      this.userDetails = null
      this.graphPhoto = null
      await auth.clearLocal()
      this.isAuthenticated2 = false;
      this.updateUser();
    },
    async fullLogOut() {
      if (auth) {
        await auth.logout()
        this.user = null
        this.graphDetails = null
        this.userDetails = null
        this.graphPhoto = null
        this.isAuthenticated2 = false;
        this.updateUser();
      }
    },
    updateUser() {
      this.user = auth.user();
      this.isAuthenticated();
    },
  }
};
</script>

<style scoped>
.navbar-wrapper {
  position: sticky;
  top: 0;
  z-index: 1030;
}

.app-navbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1.25rem;
  height: 56px;
  background: var(--bg-surface);
  border-bottom: 1px solid var(--border-default);
}

.navbar-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.navbar-logo {
  height: 28px;
  width: auto;
}

.navbar-divider {
  width: 1px;
  height: 20px;
  background: var(--border-input);
}

.navbar-app-name {
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--text-muted);
}

.navbar-right {
  display: flex;
  align-items: center;
  gap: 4px;
  position: relative;
}

/* Theme toggle */
.theme-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: none;
  color: var(--text-muted);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.theme-toggle:hover {
  background: var(--bg-hover);
  color: var(--text-primary);
}

.navbar-user {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease;
  user-select: none;
}

.navbar-user:hover {
  background: var(--bg-hover);
}

.navbar-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--accent);
  color: var(--accent-text);
  font-size: 0.75rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 0.02em;
  flex-shrink: 0;
}

.navbar-username {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-secondary);
}

.navbar-chevron {
  color: var(--text-faint);
  transition: transform 0.2s ease;
}

.navbar-chevron.open {
  transform: rotate(180deg);
}

/* Dropdown */
.navbar-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  width: 280px;
  background: var(--bg-surface);
  border: 1px solid var(--border-default);
  border-radius: 12px;
  box-shadow: 0 10px 40px var(--shadow-md), 0 2px 8px var(--shadow-sm);
  overflow: hidden;
  z-index: 1050;
}

.dropdown-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
}

.dropdown-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--accent);
  color: var(--accent-text);
  font-size: 0.85rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.dropdown-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.dropdown-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dropdown-email {
  font-size: 0.78rem;
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dropdown-divider {
  height: 1px;
  background: var(--border-default);
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  color: var(--text-secondary);
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.1s ease;
  text-align: left;
}

.dropdown-version {
  padding: 0.4rem 0.9rem 0.6rem;
  font-size: 0.7rem;
  color: var(--text-faint);
  border-top: 1px solid var(--border-default);
  margin-top: 0.25rem;
  letter-spacing: 0.02em;
  user-select: text;
}

.dropdown-item:hover {
  background: var(--bg-hover);
}

.dropdown-item svg {
  color: var(--text-muted);
}

/* Dropdown animation */
.dropdown-fade-enter-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.dropdown-fade-leave-active {
  transition: opacity 0.1s ease, transform 0.1s ease;
}

.dropdown-fade-enter,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Mobile */
@media (max-width: 640px) {
  .navbar-username,
  .navbar-app-name,
  .navbar-divider {
    display: none;
  }

  .app-navbar {
    padding: 0 1rem;
  }

  .navbar-dropdown {
    width: 260px;
  }
}
</style>
